import { feedbackcard, YesObject } from '@yes.technology/react-toolkit'
import uniqBy from 'lodash/uniqBy'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { PatchObjectResponse } from 'shared/types/object.type'
import { useSelectAndLink } from 'site/Renderer/components/SelectAndLink'
import { SelectAndLinkValue } from 'site/Renderer/components/SelectAndLink/SelectAndLink.types'
import { useSiteState } from 'siteState/shared'
import { relationObjectsAtColumn } from '../../lib/relationObjectsAtColumn'

type Params = {
  currentField: string
  serverFieldValues: YesObject | undefined
  unsavedObjects: YesObject[]
  relationWorkingSet: YesObject[]
  changeAndSave: (
    fieldColumn: string,
    value: YesObject[string]
  ) => Promise<PatchObjectResponse | undefined>
  onChange: (fieldColumn: string, value: YesObject[string]) => void
}

const useRelationClassRelationUnlink = ({
  currentField,
  serverFieldValues,
  unsavedObjects,
  relationWorkingSet,
  changeAndSave,
  onChange
}: Params) => {
  const [, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )
  const { removeSelectedOptionByUuid } = useSelectAndLink()
  const { t } = useTranslation()

  return useCallback(
    async (objects: SelectAndLinkValue[]) => {
      if (!currentField) return

      setLockObjectViewObject(true)

      const serverRelation = relationObjectsAtColumn(
        serverFieldValues,
        currentField
      )
      const serverUuids = new Set(serverRelation.map((o) => o.uuid))

      const { savedRelations, unsavedRelations } = objects.reduce(
        (
          acc: {
            savedRelations: SelectAndLinkValue[]
            unsavedRelations: SelectAndLinkValue[]
          },
          object
        ) => {
          if (serverUuids.has(object.uuid)) {
            acc.savedRelations.push(object)
          } else {
            acc.unsavedRelations.push(object)
          }

          return acc
        },
        {
          savedRelations: [],
          unsavedRelations: []
        }
      )
      const unlinkedUuids = new Set(objects.map((object) => object.uuid))

      if (savedRelations.length > 0) {
        const unsavedUuids = new Set(
          unsavedObjects.map((object) => object.uuid)
        )
        const persistedInWorkingSet = relationWorkingSet.filter(
          (relatedObject) =>
            serverUuids.has(relatedObject.uuid) &&
            !unsavedUuids.has(relatedObject.uuid)
        )
        const response = await changeAndSave(
          currentField,
          persistedInWorkingSet.filter(
            (relatedObject) =>
              !savedRelations.some(
                (object) => object.uuid === relatedObject.uuid
              )
          )
        )
        if (!response?.success) return
      }

      if (savedRelations.length > 0) {
        savedRelations.forEach((object) => {
          removeSelectedOptionByUuid(object.uuid, currentField)
        })
      }

      if (unsavedRelations.length > 0) {
        unsavedRelations.forEach((object) => {
          removeSelectedOptionByUuid(object.uuid, currentField)
        })

        if (savedRelations.length === 0) {
          feedbackcard(t('data-tables.success'), {
            type: 'success',
            message: t('data-tables.success')
          })
        }
      }

      const nextRelation = relationWorkingSet.filter(
        (relatedObject) => !unlinkedUuids.has(relatedObject.uuid)
      )
      onChange(currentField, uniqBy(nextRelation, 'uuid'))
    },
    [
      currentField,
      setLockObjectViewObject,
      serverFieldValues,
      unsavedObjects,
      relationWorkingSet,
      changeAndSave,
      removeSelectedOptionByUuid,
      t,
      onChange
    ]
  )
}

export default useRelationClassRelationUnlink
